import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import UndoIcon from "@mui/icons-material/Undo";
import { toast } from "react-toastify";

import { apiClient } from "../../../../api/httpClient";
import TablePaginationActions from "../../../../globals/components/TablePaginationActions";

const TYPE_LABEL = {
  portal_user: "Portal User",
  law_firm: "Law Firm",
  professional_guardian: "Professional Guardian",
};

const humanizeKey = (key) =>
  key
    .replaceAll("_", " ")
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const getStatusChip = (undoneAt, undoExpired) => {
  if (undoneAt) {
    return <Chip size="small" label="Undone" color="default" variant="outlined" />;
  }
  if (undoExpired) {
    return <Chip size="small" label="Expired" color="warning" variant="outlined" />;
  }
  return <Chip size="small" label="Active" color="success" variant="outlined" />;
};

const MergeLogTab = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoSubmitting, setUndoSubmitting] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      if (filterType) params.merge_type = filterType;
      const data = await apiClient("/duplicate-accounts/merge-log", "GET", {}, params);
      setLogs(Array.isArray(data?.results) ? data.results : []);
    } catch (err) {
      console.error("Failed to fetch merge log", err);
      toast.error("Failed to load merge log");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleUndoConfirm = async () => {
    if (!undoTarget) return;
    setUndoSubmitting(true);
    try {
      await apiClient(`/duplicate-accounts/merge-log/${undoTarget.merge_id}/undo`, "POST");
      toast.success("Merge has been undone.");
      setUndoTarget(null);
      fetchLogs();
    } catch (err) {
      toast.error(err?.detail || err?.message || "Undo failed");
    } finally {
      setUndoSubmitting(false);
    }
  };

  const pagedLogs = logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="merge-type-filter">Merge type</InputLabel>
          <Select
            labelId="merge-type-filter"
            value={filterType}
            label="Merge type"
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All types</MenuItem>
            <MenuItem value="portal_user">Portal users</MenuItem>
            <MenuItem value="law_firm">Law firms</MenuItem>
            <MenuItem value="professional_guardian">Professional guardians</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchLogs}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            No merge history yet.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell>When</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Keeper</TableCell>
                  <TableCell>Loser</TableCell>
                  <TableCell>What moved</TableCell>
                  <TableCell>Performed by</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedLogs.map((log) => {
                  const undoExpired = log.undo_expires_at && new Date(log.undo_expires_at) < new Date();
                  const summaryEntries = Object.entries(log.summary || {}).filter(([, v]) => Number(v) > 0);
                  return (
                    <TableRow key={log.merge_id} hover>
                      <TableCell>
                        <Typography variant="caption">{formatDate(log.performed_at)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={TYPE_LABEL[log.merge_type] || log.merge_type} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{log.keeper_label || log.keeper_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{log.loser_label || log.loser_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5, maxWidth: 360 }}>
                          {summaryEntries.length === 0 ? (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>—</Typography>
                          ) : (
                            summaryEntries.map(([k, v]) => (
                              <Chip key={k} size="small" label={`${humanizeKey(k)}: ${v}`} variant="outlined" />
                            ))
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">user #{log.performed_by}</Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(log.undone_at, undoExpired)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<UndoIcon fontSize="small" />}
                          disabled={!log.can_undo}
                          onClick={() => setUndoTarget(log)}
                        >
                          Undo
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            ActionsComponent={TablePaginationActions}
            component="div"
            count={logs.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      <Dialog open={!!undoTarget} onClose={() => !undoSubmitting && setUndoTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Undo merge?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will replay every recorded change in reverse, restoring the loser account and
            moving its data back. Run only if you know the merge was a mistake.
          </Alert>
          {undoTarget && (
            <>
              <Typography variant="body2"><strong>Keeper:</strong> {undoTarget.keeper_label || undoTarget.keeper_id}</Typography>
              <Typography variant="body2"><strong>Loser:</strong> {undoTarget.loser_label || undoTarget.loser_id}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>Performed at {formatDate(undoTarget.performed_at)}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setUndoTarget(null)} disabled={undoSubmitting}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleUndoConfirm} disabled={undoSubmitting}>
            {undoSubmitting ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Undoing…</> : "Undo merge"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MergeLogTab;
